import axios, {AxiosPromise} from 'axios'
import {envVars} from '@/envVars.ts'
import {z, ZodError, ZodSchema} from 'zod'
import {useAuthStore} from './features/auth/store/useAuthStore'
import {LocalStorageManager} from './utilities/localStorage'

const axiosConfig = {
    baseURL: envVars.VITE_API_ENDPOINT,
    headers: {
        common: {
            Authorization: useAuthStore.getState().accessToken
                ? `Bearer ${useAuthStore.getState().accessToken}`
                : false,
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
        },
        post: {
            'Access-Control-Allow-Origin': '*'
        }
    }
}

// create axios custom instance with custom config
const axiosInstance = axios.create(axiosConfig)

const attemptRefresh = async (refreshToken: string) => {
    const url = '/auth/refresh'
    try {
        const {data} = await axiosInstance.post(url, {token: refreshToken})
        LocalStorageManager.accessToken.set(data.accessToken)
        LocalStorageManager.refreshToken.set(data.refreshToken)
        useAuthStore.setState(() => ({
            accessToken: data.accessToken,
            refreshToken: data.refreshToken
        }))

        return {data}
    } catch (error) {
        if (axios.isAxiosError(error)) {
            LocalStorageManager.accessToken.remove()
            LocalStorageManager.refreshToken.remove()
            useAuthStore.setState(() => ({
                accessToken: '',
                refreshToken: ''
            }))
        } else {
            console.error(error)
        }
    }
}

axiosInstance.interceptors.request.use(request => {
    const token = LocalStorageManager.accessToken.get()
    if (token) request.headers['Authorization'] = `Bearer ${token}`
    return request
})

const endpointToIgnore = ['/login']

axiosInstance.interceptors.response.use(
    response => {
        // pass response without change
        return response
    },
    async error => {
        // get error info
        const statusCode = error?.response?.status
        const originalRequest = error.config

        switch (statusCode) {
            case 403: {
                useAuthStore.getState().reset()
                LocalStorageManager.accessToken.remove()
                LocalStorageManager.refreshToken.remove()
                if (error?.response?.data?.code === 'no-active-resellers') {
                    window.location.href = '/inactive-resellers'
                } else {
                    window.location.href = '/access-denied'
                }
                return Promise.reject(error)
            }
            case 401: {
                const refreshToken = localStorage.getItem('refreshToken')
                if (refreshToken && !originalRequest._retry && !endpointToIgnore.includes(error.config.url)) {
                    originalRequest._retry = true
                    await attemptRefresh(refreshToken)
                    return axiosInstance.request(originalRequest)
                } else {
                    useAuthStore.getState().reset()
                    LocalStorageManager.accessToken.remove()
                    return Promise.reject(error)
                }
            }

            case 500: {
                const tokenErrors = ['TokenExpiredError', 'JsonWebTokenError']
                if (tokenErrors.includes(error?.response?.data?.name)) {
                    const refreshToken = useAuthStore.getState().refreshToken
                    LocalStorageManager.refreshToken.remove()
                    if (refreshToken && !originalRequest._retry && !endpointToIgnore.includes(error.config.url)) {
                        originalRequest._retry = true // prevent infinite retry loop
                        await attemptRefresh(refreshToken)
                        return axiosInstance.request(originalRequest)
                    } else {
                        LocalStorageManager.accessToken.remove()
                        useAuthStore.setState(() => ({}))
                        return Promise.reject(error)
                    }
                } else {
                    return Promise.reject(error)
                }
            }
            default:
                return Promise.reject(error)
        }
    }
)

export default axiosInstance

/**
 * This function parses the api response using the provided shape and returns the response data or the ZodError if the parse fails
 * */
export const parseAxiosPromise = <T extends ZodSchema>({
    axiosPromise,
    responseShape,
    onZodError
}: {
    axiosPromise: AxiosPromise
    responseShape: T
    onZodError?: (error: ZodError) => void
}): Promise<z.infer<T>> =>
    axiosPromise.then(response => {
        const safeParsedResponseData = responseShape.safeParse(response.data)
        if (!safeParsedResponseData.success) {
            console.error(safeParsedResponseData.error.message)
            onZodError?.(safeParsedResponseData.error)
            throw new Error(safeParsedResponseData.error.message)
        }

        return response.data
    })
